import {
    getCategoryWithGreatestResults,
    getSearchUiState,
    getSelectedOptionFacetValues,
    getUpcomingAuctions,
} from './search';
import { LOAD_CATEGORY_RESULTS_FAIL, LOAD_CATEGORY_RESULTS_REQUEST, LOAD_CATEGORY_RESULTS_SUCCESS } from './actions';
import api from '@/redux/api/search';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import facetTypes from '@/utils/search/facetTypes';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { getItem } from './item';
import { getUserExcludedFacets } from './searchExclusions';
import { GlobalState } from '../rootReducer';
import { isArchivedSearch } from '@/utils/queryParams';
import { searchPromoted } from './searchPromoted';

// reducer
const DEFAULT_STATE = {
    metaData: {
        pageLabel: '',
        pageMetaDescription: '',
        pageMetaKeywords: '',
        pageTitle: '',
    },
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_CATEGORY_RESULTS_SUCCESS:
            let metaData = state.metaData;

            if (action.payload.landingPageMetaData) {
                metaData = {
                    // @ts-ignore
                    canonicalURL: action.payload.landingPageMetaData
                        ? action.payload.landingPageMetaData.canonicalURL
                        : '',
                    pageLabel: action.payload.landingPageMetaData.label,
                    pageMetaDescription: action.payload.landingPageMetaData.metaDescription,
                    pageMetaKeywords: action.payload.landingPageMetaData.metaKeywords,
                    pageTitle: action.payload.landingPageMetaData.pageTitle,
                };
            }

            return {
                ...state,
                metaData,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.landingPage;

const metaDataSelector = createSelector(stateSelector, (state) => state.metaData);

const getPageMetaDescription = createSelector(metaDataSelector, (metaData) => metaData.pageMetaDescription);

const getPageMetaKeywords = createSelector(metaDataSelector, (metaData) => metaData.pageMetaKeywords);

const getPageTitle = createSelector(metaDataSelector, (metaData) => metaData.pageTitle);
// @ts-ignore
const getCanonicalURL = createSelector(metaDataSelector, (metaData) => metaData.canonicalURL);

export const getPageLabel = createSelector(metaDataSelector, (metaData) => metaData.pageLabel);

export const getCategoryLandingPageUrl = (params: any) => {
    return getLandingPageUrl(params, 'c');
};

export const getAuctionResultsLandingPageUrl = (params: any) => {
    return getLandingPageUrl(params, 'price-guide');
};

const getLandingPageUrl = (params, route) => {
    if (!params.categoryId || Number.isNaN(Number(params.categoryId))) {
        return `/${route}/`;
    } else if (params.categoryId2 && params.categoryName2) {
        return `/${route}/${params.categoryName}/${params.categoryName2}/${params.categoryId}/${params.categoryId2}/`;
    }
    return `/${route}/${params.categoryName}/${params.categoryId}/`;
};

export const getLandingPageMetaData = (state: GlobalState, params: any, pathname: any, queryParams: any) => {
    const { totalFound } = getSearchUiState(state);
    const canonicalURL = getCanonicalURL(state);
    const pageMetaDescription = getPageMetaDescription(state);
    const pageMetaKeywords = getPageMetaKeywords(state);
    const archivedSearch = isArchivedSearch(queryParams, pathname);
    const pageTitle = getPageTitle(state);
    const pageUrl = getCategoryLandingPageUrl(params);
    const categoryWithGreatestResults = getCategoryWithGreatestResults(state, archivedSearch);
    const totalUpcomingAuctions = getUpcomingAuctions(state, archivedSearch);
    const selectedCreator = getSelectedOptionFacetValues(state, archivedSearch, facetTypes.CREATOR_ID);
    const selectedMaterials = getSelectedOptionFacetValues(
        state,
        archivedSearch,
        facetTypes.MATERIALS_AND_TECHNIQUES_ID
    );
    const selectedStyles = getSelectedOptionFacetValues(state, archivedSearch, facetTypes.STYLE_PERIOD_ID);
    const selectedOrigin = getSelectedOptionFacetValues(state, archivedSearch, facetTypes.ORIGIN_ID);
    const selectedCategory = getSelectedOptionFacetValues(state, archivedSearch, facetTypes.CATEGORY_ID);

    const landingPageMetaData = {
        canonicalURL,
        categoryWithGreatestResults,
        pageMetaDescription,
        pageMetaKeywords,
        pageTitle,
        pageUrl,
        selectedCategory,
        selectedCreator,
        selectedMaterials,
        selectedOrigin,
        selectedStyles,
        totalFound,
        totalUpcomingAuctions,
    };

    return cloneDeep(landingPageMetaData);
};

/* ACTION CREATORS */
const categoryResultsFail = (searchQuery, error) => ({
    error: true,
    meta: { searchQuery },
    payload: error,
    type: LOAD_CATEGORY_RESULTS_FAIL,
});

const categoryResultsRequest = (searchQuery) => ({
    payload: searchQuery,
    type: LOAD_CATEGORY_RESULTS_REQUEST,
});

const categoryResultsSuccess = (results) => ({
    payload: results,
    type: LOAD_CATEGORY_RESULTS_SUCCESS,
});

export const searchCategory = (searchQuery: any, ids: any, pageName?: any) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);

        dispatch(categoryResultsRequest(searchQuery));

        // Annotate facets which have user-defined search exclusions
        const searchExclusions = getUserExcludedFacets(state);
        const augmentedOptions = !searchExclusions
            ? searchQuery.options
            : Object.keys(searchExclusions).reduce(
                  (options, facetWithExclusions) => ({
                      ...options,
                      [facetWithExclusions]: [
                          {
                              exclude: searchExclusions[facetWithExclusions] || [],
                              include: (searchQuery.options && searchQuery.options[facetWithExclusions]) || [],
                          },
                      ],
                  }),
                  { ...searchQuery.options }
              );
        searchQuery.categories = ids?.length ? ids.map((id) => `${id}`) : searchQuery.categories;
        const augmentedSearchQuery = {
            ...searchQuery,
            buyNow: Boolean(pageName) && !pageName.includes('price-guide'),
            fullResults: true,
            options: augmentedOptions,
        };

        if (augmentedSearchQuery?.options?.auctionHouse) {
            augmentedSearchQuery?.options?.auctionHouse[0]?.exclude?.slice(0, 200);
        }

        const [results] = await Promise.all([
            api.fetchCategoryResults({
                authToken,
                deployment,
                ids,
                searchQuery: augmentedSearchQuery,
            }),
            dispatch(searchPromoted({ ids, pageName, searchQuery })),
        ]);

        if (results.error) {
            dispatch(categoryResultsFail(searchQuery, results.payload));
        } else {
            if (Boolean(augmentedSearchQuery?.searchTerm)) {
                results.payload.sort = [];
            }
            dispatch(categoryResultsSuccess(results.payload));
        }
    } catch (error) {
        dispatch(categoryResultsFail(searchQuery, error));
    }
};

export const getDeletedItemTitleIfNeeded = (state: GlobalState, query: any) => {
    let item;
    let returnTitle = '';
    if (query.deleted && !isNaN(query.deleted)) {
        item = getItem(state, query.deleted) || {
            isDeleted: true,
            title: 'The item you were seeking',
        };
    }
    if (item && item.isDeleted && item.title) {
        returnTitle = item.title;
    }
    return returnTitle;
};

//TODO: rename all the categories to landing where appropriate
